import { Box } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextFieldCompnent from "../../../components/textFieldComponent";
import RadioButtonComponent from "../../../components/radioButtonComponent";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import ButtonComponent from "../../../components/ButtonComponent";
import {
  fetchCategories,
  selectAllCategories,
} from "../../../features/categorySlice";
import { getProducts, selectAllProducts } from "../../../features/productSlice";
import {
  CouponType,
  DaysOfWeekOptions,
  TargetType,
  TargetTypeOptions,
} from "../../../models/coupon.model";
import SelectComponent from "../../../components/selectComponent";
import SelectListUI from "../../../components/selectListUI";
import DatepickerFilter from "../../../components/filters/datepickerFilter";
import dayjs from "dayjs";
import TimeTextField from "../../../components/timeTextField";
import MultipleSelectComponent from "../../../components/multipleSelectComponent";
import { useTranslation } from "react-i18next";

const CouponForm = ({
  buttonText,
  handleFormSubmit,
  initialValues,
  loading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { restaurantId } = useRestaurant();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const categories = useSelector(selectAllCategories);
  const products = useSelector(selectAllProducts);
  const todayCheck = new Date();
  todayCheck.setHours(0, 0, 0, 0);

  const couponSchema = yup.object().shape({
    code: yup.string().required("Code est requis"),
    couponType: yup
      .string()
      .required("Le type de coupon est requis")
      .oneOf(Object.values(CouponType), "Type de coupon invalide"),
    couponValue: yup
      .number()
      .transform((value, originalValue) =>
        typeof originalValue === "string" ? Number(originalValue) : value
      )
      .typeError("Valeur invalide")
      .required("Le prix est requis")
      .moreThan(0, "La valeur doit être supérieure à 0")
      .when("couponType", (couponType, schema) => {
        const type = Array.isArray(couponType) ? couponType[0] : couponType;
        if (type === CouponType.percentage) {
          return schema.max(
            100,
            "La valeur ne peut pas dépasser 100 pour un pourcentage"
          );
        }
        return schema;
      }),
    minOrderAmount: yup
      .number()
      .min(0, "Montant minimum de commande est requis"),
    couponCategories: yup.array().when("targetType", {
      is: (val) => val === "categories" || val === "categoryProducts",
      then: (schema) =>
        schema
          .min(1, "Au moins une catégorie est requise")
          .required("Catégorie est requise"),
      otherwise: (schema) => schema.notRequired(),
    }),
    couponProducts: yup.array().when("targetType", {
      is: (val) => val === "products" || val === "categoryProducts",
      then: (schema) =>
        schema
          .min(1, "Au moins un produit est requis")
          .required("Produit est requis"),
      otherwise: (schema) => schema.notRequired(),
    }),
    excludeProducts: yup.array(),
    recurringDays: yup
      .array()
      .of(yup.number().min(0).max(6).typeError("Jour invalide"))
      .notRequired(),
    isActive: yup.boolean(),
    startDate: yup
      .mixed()
      .nullable()
      .notRequired(),
    endDate: yup
      .mixed()
      .nullable()
      .notRequired()
      .test(
        "end-date-after-start",
        "La date de fin ne peut pas être avant la date de début",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return new Date(value) >= new Date(startDate);
        }
      ),
    startTime: yup.string().nullable().notRequired(),
    endTime: yup
      .string()
      .nullable()
      .notRequired()
      .test(
        "end-time-validation",
        "L'heure de fin doit être après l'heure de début",
        function (endTime) {
          const { startTime, startDate, endDate } = this.parent;
          if (!startTime || !endTime) return true;

          const startDateTime = new Date(`1970-01-01T${startTime}:00`);
          const endDateTime = new Date(`1970-01-01T${endTime}:00`);

          if (
            startDate &&
            endDate &&
            new Date(startDate).toDateString() ===
            new Date(endDate).toDateString()
          ) {
            if (endDateTime <= startDateTime) {
              return this.createError({
                message: "L'heure de fin doit être après l'heure de début",
              });
            }
          }

          return true;
        }
      ),
  });

  useEffect(() => {
    dispatch(fetchCategories(restaurantId));
    dispatch(getProducts(restaurantId));
  }, [dispatch, restaurantId]);

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={couponSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => {
        const showCategories =
          values.targetType === TargetType.CATEGORIES ||
          values.targetType === TargetType.CATEGORY_PRODUCTS;

        const showProducts =
          values.targetType === TargetType.PRODUCTS ||
          values.targetType === TargetType.CATEGORY_PRODUCTS;

        let currentRow = 2;

        return (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="16px"
              gridTemplateColumns="repeat(6, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 6" },
              }}
            >
              <TextFieldCompnent
                type="text"
                label={t("fields.code")}
                change={handleChange}
                value={values.code}
                name="code"
                blur={handleBlur}
                touched={touched.code}
                error={errors.code}
                colum="span 3"
                row="1 / span 1"
                required
              />
              <SelectComponent
                name="targetType"
                label={t("fields.targetType")}
                items={TargetTypeOptions}
                value={values.targetType}
                change={(e) => {
                  handleChange(e);
                  setFieldValue("couponCategories", []);
                  setFieldValue("couponProducts", []);
                }}
                gridColumn="span 3"
                gridRow="2 / span 1"
              />
              {showCategories && (
                <SelectListUI
                  name="couponCategories"
                  title={t("fields.selectCategories")}
                  items={categories}
                  value={values.couponCategories}
                  change={handleChange}
                  gridColumn="span 3"
                  gridRow={`${++currentRow} / span 1`}
                  error={errors.couponCategories}
                  touched={touched.couponCategories}
                />
              )}
              {showProducts && (
                <SelectListUI
                  name="couponProducts"
                  title={t("fields.selectProducts")}
                  items={products}
                  value={values.couponProducts}
                  change={(e) => {
                    handleChange(e);

                    // Ensure a product can't be both included and excluded
                    const selected = e.target.value;
                    const overlap = selected.filter((id) =>
                      values.excludeProducts.includes(id)
                    );
                    if (overlap.length > 0) {
                      setFieldValue(
                        "excludeProducts",
                        values.excludeProducts.filter(
                          (id) => !selected.includes(id)
                        )
                      );
                    }
                  }}
                  gridColumn="span 3"
                  gridRow={`${++currentRow} / span 1`}
                  error={errors.couponProducts}
                  touched={touched.couponProducts}
                />
              )}
              <SelectListUI
                name="excludeProducts"
                title={t("fields.selectExcludedProducts")}
                items={products}
                value={values.excludeProducts}
                change={(e) => {
                  handleChange(e);

                  // Ensure a product can't be both included and excluded
                  const selected = e.target.value;
                  const overlap = selected.filter((id) =>
                    values.couponProducts.includes(id)
                  );
                  if (overlap.length > 0) {
                    setFieldValue(
                      "couponProducts",
                      values.couponProducts.filter(
                        (id) => !selected.includes(id)
                      )
                    );
                  }
                }}
                gridColumn="span 3"
                gridRow={`${++currentRow} / span 1`}
                error={errors.excludeProducts}
                touched={touched.excludeProducts}
              />
              <RadioButtonComponent
                change={handleChange}
                colum="span 3"
                row={`${++currentRow} / span 1`}
                name="couponType"
                radioText1={t("filters.fixed")}
                radioText2={t("filters.percentage")}
                radioValue1={CouponType.fixed}
                radioValue2={CouponType.percentage}
                text={t("fields.couponType")}
                value={values.couponType}
                blur={handleBlur}
                touched={touched.couponType}
                error={errors.couponType}
                required
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.couponValue")}
                change={handleChange}
                value={values.couponValue}
                name="couponValue"
                blur={handleBlur}
                touched={touched.couponValue}
                error={errors.couponValue}
                colum="span 3"
                row={`${++currentRow} / span 1`}
                num={0}
                required
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.minOrder")}
                change={handleChange}
                value={values.minOrderAmount}
                name="minOrderAmount"
                blur={handleBlur}
                touched={touched.minOrderAmount}
                error={errors.minOrderAmount}
                colum="span 3"
                row={`${++currentRow} / span 1`}
                num={0}
              />
              <TextFieldCompnent
                type="number"
                label={t("fields.maxUses")}
                change={handleChange}
                value={values.limit}
                name="limit"
                blur={handleBlur}
                touched={touched.limit}
                error={errors.limit}
                colum="span 3"
                row={`${++currentRow} / span 1`}
                num={0}
              />
              <RadioButtonComponent
                change={handleChange}
                colum="span 3"
                row={`${++currentRow} / span 1`}
                name="isActive"
                radioText1={t("common.active")}
                radioText2={t("common.inactive")}
                radioValue1={true}
                radioValue2={false}
                text={t("fields.state")}
                value={values.isActive}
              />
              <DatepickerFilter
                label={t("fields.startDateOptional")}
                onChange={(newValue) => {
                  setFieldValue(
                    "startDate",
                    newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                  );
                }}
                value={values.startDate ? values.startDate : ""}
                name="startDate"
                handleBlur={handleBlur}
                touched={touched.startDate}
                error={errors.startDate}
                column="span 2"
                row={`${++currentRow} / span 1`}
                minDate={dayjs()}
                maxDate={
                  values.endDate && dayjs(values.endDate).isBefore(dayjs())
                    ? dayjs(values.endDate)
                    : null
                }
              />
              <TimeTextField
                label={t("fields.startTime")}
                value={values.startTime}
                onChange={handleChange}
                handleBlur={handleBlur}
                touched={touched.startTime}
                error={errors.startTime}
                name="startTime"
                column="span 1"
                row={`${currentRow} / span 1`}
                disabled={false}
              />
              <DatepickerFilter
                label={t("fields.endDateOptional")}
                onChange={(newValue) => {
                  setFieldValue(
                    "endDate",
                    newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                  );
                }}
                value={values.endDate ? values.endDate : ""}
                name="endDate"
                handleBlur={handleBlur}
                touched={touched.endDate}
                error={errors.endDate}
                column="span 2"
                row={`${++currentRow} / span 1`}
                minDate={values.startDate ? dayjs(values.startDate) : dayjs()}
              />
              <TimeTextField
                label={t("fields.endTime")}
                value={values.endTime}
                onChange={handleChange}
                handleBlur={handleBlur}
                touched={touched.endTime}
                error={errors.endTime}
                name="endTime"
                column="span 1"
                row={`${currentRow} / span 1`}
                disabled={false}
              />
              <MultipleSelectComponent
                name="recurringDays"
                label={t("fields.recurringDays")}
                items={DaysOfWeekOptions}
                value={values.recurringDays}
                change={handleChange}
                touched={touched.recurringDays}
                error={errors.recurringDays}
                gridColumn="span 3"
                gridRow={`${++currentRow} / span 1`}
              />
            </Box>

            <ButtonComponent text={buttonText} loading={loading} />
          </form>
        );
      }}
    </Formik>
  );
};

export default CouponForm;
